import type { Project, Equipment, AMIDataPoint } from '../types/project';
import { supabase } from './supabase';
import { deleteAmi } from './amiStore';

export interface LocalProject {
  project: Partial<Project>;
  equipment: Equipment[];
  amiData: AMIDataPoint[];
  projectId: string;
  savedAt: string;
  userId: string;
}

const STORAGE_KEY_PREFIX = 'trueload_projects_';
const CURRENT_PROJECT_KEY_PREFIX = 'trueload_current_project_id_';
const LAST_PROJECT_KEY_PREFIX = 'trueload_last_project_id_';

function getStorageKey(userId: string): string {
  return `${STORAGE_KEY_PREFIX}${userId}`;
}

function getCurrentProjectKey(userId: string): string {
  return `${CURRENT_PROJECT_KEY_PREFIX}${userId}`;
}

function getLastProjectKey(userId: string): string {
  return `${LAST_PROJECT_KEY_PREFIX}${userId}`;
}

async function getUserId(): Promise<string | null> {
  const { data: { session } } = await supabase.auth.getSession();
  return session?.user?.id ?? null;
}

export function getLastProjectId(userId: string): string | null {
  return localStorage.getItem(getLastProjectKey(userId));
}

export function setLastProjectId(userId: string, projectId: string): void {
  localStorage.setItem(getLastProjectKey(userId), projectId);
}

export function clearLastProjectId(userId: string, onlyIfMatches?: string): void {
  if (onlyIfMatches) {
    const current = localStorage.getItem(getLastProjectKey(userId));
    if (current !== onlyIfMatches) return;
  }
  localStorage.removeItem(getLastProjectKey(userId));
}

export async function saveProjectLocally(
  projectId: string,
  project: Partial<Project>,
  equipment: Equipment[],
  _amiData: AMIDataPoint[],
  userId?: string
): Promise<void> {
  try {
    let uid = userId ?? null;
    if (!uid) {
      const { data: { session } } = await supabase.auth.getSession();
      uid = session?.user?.id ?? null;
    }
    if (!uid) {
      console.warn('Cannot save project locally: no user logged in');
      return;
    }

    const projects = await getAllLocalProjects();

    // Raw AMI points are stored in IndexedDB (amiStore.ts), not here.
    // localStorage holds only project metadata and equipment; amiData is
    // intentionally written as an empty array to keep payload small.
    const localProject: LocalProject = {
      project,
      equipment,
      amiData: [],
      projectId,
      savedAt: new Date().toISOString(),
      userId: uid,
    };

    projects[projectId] = localProject;
    const serialized = JSON.stringify(projects);
    localStorage.setItem(getStorageKey(uid), serialized);
    localStorage.setItem(getCurrentProjectKey(uid), projectId);
  } catch (error) {
    console.error('Error saving project locally:', error);
  }
}

export async function loadProjectLocally(projectId: string): Promise<LocalProject | null> {
  try {
    const projects = await getAllLocalProjects();
    return projects[projectId] || null;
  } catch (error) {
    console.error('Error loading project locally:', error);
    return null;
  }
}

export async function getAllLocalProjects(): Promise<Record<string, LocalProject>> {
  try {
    const userId = await getUserId();
    if (!userId) {
      return {};
    }
    const data = localStorage.getItem(getStorageKey(userId));
    return data ? JSON.parse(data) : {};
  } catch (error) {
    console.error('Error getting all local projects:', error);
    return {};
  }
}

export async function deleteLocalProject(projectId: string): Promise<void> {
  try {
    const userId = await getUserId();
    if (!userId) {
      return;
    }

    const projects = await getAllLocalProjects();
    delete projects[projectId];
    localStorage.setItem(getStorageKey(userId), JSON.stringify(projects));

    const currentId = localStorage.getItem(getCurrentProjectKey(userId));
    if (currentId === projectId) {
      localStorage.removeItem(getCurrentProjectKey(userId));
    }
    clearLastProjectId(userId, projectId);
  } catch (error) {
    console.error('Error deleting local project:', error);
  }
}

/**
 * Fully remove a project's browser-side footprint: the localStorage entry AND
 * the IndexedDB AMI record.
 *
 * This is the DELETE primitive. deleteLocalProject is the MOVE primitive — the
 * local_ -> UUID promotion paths in App.tsx call it to retire a temporary local
 * entry while the AMI record is still needed, and must keep calling it. Do not
 * merge these two functions.
 */
export async function purgeLocalProject(projectId: string): Promise<void> {
  await deleteLocalProject(projectId);
  try {
    await deleteAmi(projectId);
  } catch (amiError) {
    console.error('Error deleting local AMI data:', amiError);
  }
}

export async function getCurrentProjectId(): Promise<string | null> {
  const userId = await getUserId();
  if (!userId) {
    return null;
  }
  return localStorage.getItem(getCurrentProjectKey(userId));
}

export async function setCurrentProjectId(projectId: string | null): Promise<void> {
  const userId = await getUserId();
  if (!userId) {
    return;
  }
  if (projectId) {
    localStorage.setItem(getCurrentProjectKey(userId), projectId);
  } else {
    localStorage.removeItem(getCurrentProjectKey(userId));
  }
}

export function generateLocalProjectId(): string {
  return `local_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
}

export async function clearAllLocalProjects(): Promise<void> {
  try {
    const userId = await getUserId();
    if (!userId) {
      return;
    }

    localStorage.removeItem(getStorageKey(userId));
    localStorage.removeItem(getCurrentProjectKey(userId));
    console.log(`Cleared all local projects for user ${userId}`);
  } catch (error) {
    console.error('Error clearing local projects:', error);
  }
}

export async function migrateOldLocalStorage(): Promise<void> {
  try {
    const userId = await getUserId();
    if (!userId) {
      return;
    }

    // Remove legacy per-key format (pre-map era). These are unowned at this
    // point — projects are authoritative in Supabase.
    if (localStorage.getItem('trueload_project')) {
      localStorage.removeItem('trueload_project');
      localStorage.removeItem('trueload_equipment');
      localStorage.removeItem('trueload_ami_data');
      localStorage.removeItem('trueload_project_id');
    }

    // IMPORTANT: Do NOT migrate the old unscoped 'trueload_projects' key to
    // the current user. That key could belong to any user who logged in before
    // scoping was introduced. Blindly assigning it to whoever logs in first
    // causes cross-account data leakage. Just delete it; Supabase is the
    // source of truth for cloud-synced projects.
    if (localStorage.getItem('trueload_projects')) {
      localStorage.removeItem('trueload_projects');
    }
    if (localStorage.getItem('trueload_current_project_id')) {
      localStorage.removeItem('trueload_current_project_id');
    }
  } catch (error) {
    console.error('Error migrating old localStorage:', error);
  }
}

/**
 * Remove any projects from the current user's scoped localStorage that have
 * a userId field belonging to a different user. This cleans up data that was
 * incorrectly migrated by earlier versions of migrateOldLocalStorage().
 */
export async function sanitizeLocalProjects(): Promise<void> {
  try {
    const userId = await getUserId();
    if (!userId) return;

    const storageKey = getStorageKey(userId);
    const raw = localStorage.getItem(storageKey);
    if (!raw) return;

    const projects: Record<string, LocalProject> = JSON.parse(raw);
    let dirty = false;

    for (const [id, proj] of Object.entries(projects)) {
      // If the record has an explicit userId that differs from the current
      // user, it was migrated in error — remove it.
      if (proj.userId && proj.userId !== userId) {
        delete projects[id];
        dirty = true;
      }
    }

    if (dirty) {
      localStorage.setItem(storageKey, JSON.stringify(projects));
    }
  } catch (error) {
    console.error('Error sanitizing local projects:', error);
  }
}

/**
 * The one case the orphan sweep must never act on: an empty cloud id list
 * while synced local copies exist.
 *
 * Zero rows is ambiguous — a genuinely empty account and a filtered/denied
 * read are the same response. The two readings have wildly asymmetric costs:
 * purging wrongly destroys every synced local copy and its cached AMI
 * intervals (unrecoverable when the intervals never reached the server),
 * while skipping wrongly just leaves stale entries that the existing
 * project-gone banner already handles. So refuse.
 *
 * Deliberately NOT a general "did any rows come back" check: a partial list
 * is the caller's problem (it gates on fetching every page) and a non-empty
 * list is trustworthy — RLS denial is all-or-nothing here, since a single
 * predicate governs the whole table.
 */
export function shouldSkipOrphanPurge(cloudIdCount: number, syncedLocalCount: number): boolean {
  return cloudIdCount === 0 && syncedLocalCount > 0;
}

/**
 * Remove browser-stored projects that were deleted from the cloud.
 *
 * A local entry whose id is NOT prefixed `local_` was assigned by the database,
 * which means it existed in the cloud at some point. If it is absent from the
 * authoritative cloud id list, it was deleted and the local copy is a ghost.
 *
 * DANGER: the caller MUST only invoke this when the cloud fetch actually
 * succeeded. If it is called with an empty or partial id list because a request
 * failed, every synced project will look orphaned and the entire local safety
 * net will be destroyed. The caller gates this on an explicit success flag.
 *
 * That flag is necessary but NOT sufficient: a row-level-security denial
 * returns HTTP 200 with zero rows and no error, which is indistinguishable
 * from "this account has no cloud projects" at the call site. MFA made that
 * case routine — an enrolled user's pre-verification session is aal1, where
 * the restrictive policies filter every row. So this function independently
 * refuses the all-or-nothing case; see shouldSkipOrphanPurge.
 *
 * Returns the ids that were purged.
 */
export async function purgeOrphanedLocalProjects(cloudProjectIds: Set<string>): Promise<string[]> {
  try {
    const userId = await getUserId();
    if (!userId) return [];

    const storageKey = getStorageKey(userId);
    const raw = localStorage.getItem(storageKey);
    if (!raw) return [];

    const projects: Record<string, LocalProject> = JSON.parse(raw);
    const purged: string[] = [];

    const syncedIds = Object.keys(projects).filter(id => !id.startsWith('local_'));
    if (shouldSkipOrphanPurge(cloudProjectIds.size, syncedIds.length)) {
      console.warn(
        `[orphanSweep] refusing to purge: the cloud list is empty while ${syncedIds.length} ` +
        `synced local project(s) exist. An RLS-filtered read looks identical to an empty ` +
        `account, so the destructive reading is not taken.`
      );
      return [];
    }

    for (const id of Object.keys(projects)) {
      if (id.startsWith('local_')) continue;   // never synced — not a ghost
      if (cloudProjectIds.has(id)) continue;   // still live in the cloud
      delete projects[id];
      purged.push(id);
    }

    if (purged.length > 0) {
      localStorage.setItem(storageKey, JSON.stringify(projects));
      for (const id of purged) {
        try {
          await deleteAmi(id);
        } catch (amiError) {
          console.error('Error purging orphaned AMI data:', amiError);
        }
      }
      const currentId = localStorage.getItem(getCurrentProjectKey(userId));
      if (currentId && purged.includes(currentId)) {
        localStorage.removeItem(getCurrentProjectKey(userId));
      }
      const lastId = localStorage.getItem(getLastProjectKey(userId));
      if (lastId && purged.includes(lastId)) {
        localStorage.removeItem(getLastProjectKey(userId));
      }
      console.log(`Purged ${purged.length} orphaned local project(s)`);
    }

    return purged;
  } catch (error) {
    console.error('Error purging orphaned local projects:', error);
    return [];
  }
}
